/* Bootstrap Dashboard — Superuser console.
 *
 * Reuses the Admin.jsx portal (bootstrap admin already sees all tabs) and
 * layers on top an "Owner-only Tools" strip: System Status, Environment,
 * Feature Flags, User Impersonation stub, and Danger Zone.
 *
 * Only accessible when user.is_bootstrap_admin === true — enforced by the
 * ProtectedBootstrapRoute wrapper in App.js.
 */
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ShieldAlert, Cpu, Database, Zap, KeyRound, Users2, AlertTriangle,
  Terminal, ExternalLink, Copy, CheckCircle2,
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import Admin from './Admin';

export default function BootstrapDashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [health, setHealth] = useState(null);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    api.get('/admin/overview').then(r => setHealth(r.data)).catch(() => {});
  }, []);

  const copy = (label, val) => {
    try { navigator.clipboard.writeText(val); setCopied(label); setTimeout(() => setCopied(''), 1400); } catch { toast.error('Copy failed'); }
  };

  return (
    <div className="space-y-5 font-mono" data-testid="bootstrap-dashboard-root">
      {/* Elite header */}
      <motion.div
        initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
        className="border border-amber-500/30 bg-gradient-to-r from-amber-500/5 to-transparent p-4 flex items-center gap-3 flex-wrap"
      >
        <ShieldAlert className="w-5 h-5 text-amber-400 flex-shrink-0" strokeWidth={1.5} />
        <div className="flex-1 min-w-0">
          <p className="text-[9px] uppercase tracking-[0.35em] text-amber-300/80">Platform Owner Access</p>
          <h1 className="font-mono text-xl tracking-tight text-white/95">Bootstrap Console</h1>
        </div>
        <span className="text-[9px] uppercase tracking-widest text-white/40 font-mono truncate max-w-[240px]">
          {user?.email}
        </span>
        <button
          onClick={() => nav('/admin/dashboard')}
          data-testid="btn-boot-to-admin"
          className="text-[9px] uppercase tracking-widest px-3 py-1 border border-white/15 hover:bg-white/5"
        >Admin Portal →</button>
      </motion.div>

      {/* Owner-only tools strip */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="owner-tools">
        <ToolCard icon={Cpu} label="Engine" value="2.2.0" hint="battle_engine_v2" testid="tool-engine" />
        <ToolCard icon={Database} label="Users" value={health?.users?.total ?? '—'} hint={`${health?.users?.active ?? '—'} active`} testid="tool-users" />
        <ToolCard icon={Zap} label="Battles" value={health?.battles ?? '—'} hint={`${health?.active_listings ?? '—'} listings`} testid="tool-battles" />
        <ToolCard icon={Users2} label="Suspended" value={health?.users?.suspended ?? '—'} hint="policy holds" testid="tool-suspended" />
      </section>

      {/* System configuration */}
      <section className="border border-white/10 bg-black/40 p-4" data-testid="owner-config">
        <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300/80 mb-3 flex items-center gap-2">
          <Terminal className="w-3 h-3" /> System Configuration
        </p>
        <div className="space-y-1.5 text-xs">
          <ConfigRow label="MONGO_URL" value="protected · via env" mono />
          <ConfigRow label="FRONTEND_URL" value={window.location.origin} copyLabel="frontend" onCopy={copy} copied={copied} mono />
          <ConfigRow label="BOOTSTRAP_ADMIN_EMAIL" value={user?.email || 'protected'} mono />
          <ConfigRow label="Engine version" value="battle_engine_v2 · 2.2.0" mono />
          <ConfigRow label="Balance module" value="analytics · 3.0.0" mono />
          <ConfigRow label="Email delivery" value="Resend (log-fallback if key unset)" mono />
        </div>
      </section>

      {/* Reused Admin portal — bootstrap admin gets full tab access */}
      <section className="border border-white/10 p-4" data-testid="owner-admin-portal">
        <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300/80 mb-3">Platform Management</p>
        <Admin />
      </section>

      {/* Danger zone */}
      <section className="border border-red-500/30 bg-red-500/5 p-4" data-testid="owner-danger-zone">
        <p className="text-[10px] uppercase tracking-[0.3em] text-red-300 mb-3 flex items-center gap-2">
          <AlertTriangle className="w-3 h-3" /> Danger Zone
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
          <DangerButton label="Rebuild Balance Recs" hint="Recomputes admin-approved tuning proposals"
            onClick={async () => {
              try { const r = await api.post('/admin/balance/recompute'); toast.success(`Recomputed — ${r.data.created} new`); }
              catch { toast.error('Failed'); }
            }} testid="danger-balance" />
          <DangerButton label="Invalidate All Sessions" hint="Force-logout every user (not wired — placeholder)"
            onClick={() => toast('This action is a placeholder — extend when ready', { icon: '⚠️' })}
            testid="danger-sessions" disabled />
          <DangerButton label="Impersonate User" hint="Session hijack for support (placeholder)"
            onClick={() => toast('Impersonation flow scaffold — extend when needed', { icon: '⚠️' })}
            testid="danger-impersonate" disabled />
        </div>
        <p className="text-[9px] uppercase tracking-widest text-red-300/70 mt-3">
          Every action here is written to the audit log with actor = platform owner.
        </p>
      </section>
    </div>
  );
}


function ToolCard({ icon: Icon, label, value, hint, testid }) {
  return (
    <div className="border border-white/10 bg-black/40 p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-widest text-white/50">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <p className="mt-2 font-mono text-2xl text-white/95 tabular-nums">{value}</p>
      <p className="text-[9px] uppercase tracking-widest text-white/40 mt-0.5">{hint}</p>
    </div>
  );
}

function ConfigRow({ label, value, copyLabel, onCopy, copied, mono }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-white/5 last:border-0 pb-1.5 last:pb-0">
      <span className="text-[10px] uppercase tracking-widest text-white/50">{label}</span>
      <span className={`${mono ? 'font-mono' : ''} text-white/85 truncate max-w-[300px] flex items-center gap-1.5`}>
        {value}
        {copyLabel && (
          <button onClick={() => onCopy(copyLabel, value)} data-testid={`config-copy-${copyLabel}`}
            className="text-white/40 hover:text-white/90">
            {copied === copyLabel ? <CheckCircle2 className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
          </button>
        )}
      </span>
    </div>
  );
}

function DangerButton({ label, hint, onClick, testid, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className="text-left border border-red-500/25 bg-red-500/5 p-3 hover:bg-red-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <p className="text-[11px] uppercase tracking-widest text-red-200 flex items-center justify-between">
        {label} <ExternalLink className="w-3 h-3 opacity-50" />
      </p>
      <p className="text-[10px] text-white/50 mt-1">{hint}</p>
    </button>
  );
}
